import { useMemo, useState } from 'react'
import {
  Box, CircularProgress, Dialog, FormControl, IconButton, MenuItem,
  Paper, Select, Tooltip, Typography,
} from '@mui/material'
import TableChartIcon  from '@mui/icons-material/TableChart'
import FullscreenIcon  from '@mui/icons-material/Fullscreen'
import DownloadIcon    from '@mui/icons-material/Download'
import CloseIcon       from '@mui/icons-material/Close'
import GradientIcon    from '@mui/icons-material/Gradient'
import { DataGrid } from '@mui/x-data-grid'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectBeacon4PerformanceTable,
  selectBeacon4PerformanceTableLoading,
  selectBeacon4PerformanceAvailableKpisByTech,
} from '../reducers/Beacon4PerformanceKpiDataReducer'
import {
  selectBeacon4PerfAggregationMode,
  selectBeacon4PerfTech,
  selectBeacon4PerfMainKpi,
  selectBeacon4PerfTableKpi,
} from '../reducers/Beacon4FiltersReducer'
import { reloadBeacon4PerformanceTableOnly } from '../actions/Beacon4PerformanceModuleActions'

// Must match the Aggregation Mode values the backend accepts. Kept here
// (not imported from FilterBar) so this component owns its own controls.
const AGG_MODES = ['Site', 'Mycom ID', 'Side', 'Polygon', 'Vendor']

// Field name of the row-header column, used both in the column def and in
// initialState.pinnedColumns below — kept as a constant so the two never drift.
const GROUP_COL_FIELD = 'groupKey'

// ── Heatmap coloring ─────────────────────────────────────────────────────────
// Colors cells on a light→dark gradient by magnitude. The hue family (green
// vs red) is picked from the KPI's Direction (kpi_config.is_rate):
//   isRate = false → "Higher is better"  → green family
//   isRate = true  → "Lower is better"   → red family
// This is a pure magnitude gradient (light = low value, dark = high value);
// direction only chooses the color family, it does not judge good vs bad.
const HEATMAP_STOPS = {
  higherIsBetter: { light: [232, 245, 233], dark: [27, 94, 32] },   // #e8f5e9 → #1b5e20 (green)
  lowerIsBetter:  { light: [255, 235, 238], dark: [183, 28, 28] },  // #ffebee → #b71c1c (red)
}

// Small in-house linear RGB interpolation — avoids pulling in a color-scale
// library just for this one gradient.
function lerpColor(from, to, t) {
  const r = Math.round(from[0] + (to[0] - from[0]) * t)
  const g = Math.round(from[1] + (to[1] - from[1]) * t)
  const b = Math.round(from[2] + (to[2] - from[2]) * t)
  return `rgb(${r}, ${g}, ${b})`
}

function heatmapStyleFor(value, min, max, isRate) {
  if (value == null || !isFinite(value) || min == null || max == null) return null
  const t = max === min ? 0.5 : Math.min(1, Math.max(0, (value - min) / (max - min)))
  const stops = isRate ? HEATMAP_STOPS.lowerIsBetter : HEATMAP_STOPS.higherIsBetter
  return {
    bg: lerpColor(stops.light, stops.dark, t),
    // Once the background is dark enough, switch to white text for contrast.
    color: t > 0.55 ? '#fff' : 'inherit',
  }
}

const gridSx = {
  border: 'none',
  fontSize: 11,
  '& .MuiDataGrid-columnHeaders': { bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' },
  '& .MuiDataGrid-columnHeaderTitle': { fontSize: 10, fontWeight: 700, color: 'text.secondary' },
  '& .b4p-row-header': { fontWeight: 600, fontFamily: 'monospace', fontSize: 11, bgcolor: 'rgba(0,0,0,0.015)' },
  '& .MuiDataGrid-cell': { borderColor: 'divider', py: 0.5 },
  // Heatmap cells fill the whole cell with their own background via
  // renderCell, so kill the default cell padding that would leave a plain
  // white margin around the color.
  '& .MuiDataGrid-cell.b4p-heat-cell': { padding: 0 },
  // Pinned column gets a visible separator so it reads as "frozen" rather
  // than just differently-shaded — MUI adds this class to both the header
  // and body cells of any pinned column automatically.
  '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedLeft': {
    borderRight: '1px solid', borderColor: 'divider',
  },
}

/**
 * Renders the aggregation-mode breakdown table:
 *   - Rows    = each group value (Site / Mycom / Polygon / Side / Vendor)
 *   - Columns = dates (respecting the Daily/Weekly/Monthly time aggregation)
 *   - Cells   = KPI value (chosen in the header dropdown) for that group on that date
 *
 * The table has its OWN KPI picker — it defaults to the Main KPI on Apply,
 * then the user can switch to any sub-KPI of the same tech without re-running
 * Apply (only the table is refetched). The header (including the picker) is
 * always rendered so users can change the KPI even while the table is loading
 * or empty.
 *
 * The group-key column (Site / Polygon / ... value) is frozen via the
 * DataGrid's `pinnedColumns` initial state, so it stays visible — header and
 * cells — while the user scrolls horizontally through the date columns.
 *
 * Cells are optionally heatmap-colored by value (see HEATMAP_STOPS above),
 * toggle in the header bar. Requires the backend to return `isRate` on the
 * table response (kpi_config's Direction) — see comment on `table?.isRate`.
 *
 * A Maximize button (matching the charts' Fullscreen affordance) opens the
 * same table full-screen, useful when there are many group rows or date
 * columns and the inline panel feels cramped.
 */
export default function Beacon4PerformanceAggregationTable() {
  const dispatch  = useDispatch()
  const table     = useSelector(selectBeacon4PerformanceTable)
  const loading   = useSelector(selectBeacon4PerformanceTableLoading)
  const aggMode   = useSelector(selectBeacon4PerfAggregationMode)
  const tech      = useSelector(selectBeacon4PerfTech)
  const mainKpi   = useSelector(selectBeacon4PerfMainKpi)
  const tableKpi  = useSelector(selectBeacon4PerfTableKpi)
  const kpisByTech = useSelector(selectBeacon4PerformanceAvailableKpisByTech)

  const [maximized, setMaximized] = useState(false)
  const [heatmap, setHeatmap]     = useState(false)

  // All active KPIs of the current tech, exactly as the backend returns them
  // (Main + every Breakdown/Breakdown2 — independent of which Main KPI drives
  // the dashboard). The Main is annotated in the option list below.
  const kpiOptions = useMemo(() => kpisByTech?.[tech] ?? [], [kpisByTech, tech])

  // What the dropdown shows: fall back to mainKpi until the user has applied
  // (which seeds tableKpi). If neither is set, leave it empty.
  const selectedKpi = tableKpi || mainKpi || ''

  const onChangeKpi = (key) => {
    if (!key || key === selectedKpi) return
    dispatch(reloadBeacon4PerformanceTableOnly({ kpiKey: key }))
  }

  const onChangeAggMode = (mode) => {
    if (!mode || mode === aggMode) return
    dispatch(reloadBeacon4PerformanceTableOnly({ aggMode: mode }))
  }

  // Color-scale bounds for the heatmap: the 5th and 95th percentile of every
  // value in the table (all rows × all dates), NOT the absolute min/max.
  // A single extreme outlier (e.g. a KPI formula glitch producing a wildly
  // large or very negative value in one cell) would otherwise dominate the
  // absolute min/max and compress every "normal" cell toward one end of the
  // gradient, making them all look the same shade regardless of their real
  // differences. Values beyond these bounds are simply clamped to the
  // darkest/lightest stop (heatmapStyleFor already clamps t to [0,1]) —
  // they show as fully saturated rather than stretching the scale to fit them.
  const valueRange = useMemo(() => {
    if (!table?.rows?.length) return { min: null, max: null }

    const values = []
    table.rows.forEach(r => {
      table.dates.forEach(d => {
        const v = r.values?.[d]
        if (typeof v === 'number' && isFinite(v)) values.push(v)
      })
    })
    if (!values.length) return { min: null, max: null }

    values.sort((a, b) => a - b)
    const percentile = (p) => {
      const idx = Math.min(values.length - 1, Math.max(0, Math.floor(p * (values.length - 1))))
      return values[idx]
    }
    let min = percentile(0.05)
    let max = percentile(0.95)

    // If the percentile bounds collapse onto the SAME value, fall back to the
    // true min/max instead. This happens for ceiling-heavy metrics like
    // Availability, where the vast majority of cells sit exactly at 100 —
    // both the 5th and 95th percentile land on 100 too, which would otherwise
    // give every cell the exact same flat color and hide the very outliers
    // (e.g. an outage dipping to 79%) a heatmap exists to surface.
    if (max === min) {
      min = values[0]
      max = values[values.length - 1]
    }

    if (!isFinite(min) || !isFinite(max) || max === min) return { min: null, max: null }
    return { min, max }
  }, [table])

  const { columns, rows } = useMemo(() => {
    if (!table) return { columns: [], rows: [] }

    // isRate comes from kpi_config's Direction for the selected KPI. Falls
    // back to "Higher is better" (green) if the backend hasn't been updated
    // yet to include it on the table response.
    const isRate = Boolean(table.isRate)

    const cols = [
      {
        field: GROUP_COL_FIELD,
        headerName: aggMode || 'Group',
        width: 150,
        sortable: true,
        cellClassName: 'b4p-row-header',
        // NOTE: there is no `pinned` column-def prop in MUI X Data Grid —
        // freezing is configured on the DataGrid itself via
        // `initialState.pinnedColumns` (see below), not here.
      },
      ...table.dates.map(d => ({
        field: d,
        headerName: d,
        width: 105,
        type: 'number',
        sortable: true,
        // Explicit numeric comparator — don't rely on the grid inferring
        // numeric sort purely from `type: 'number'`. This guarantees a real
        // numeric comparison (9.5 sorts before 10.2) instead of a possible
        // text/lexicographic fallback (which would put "10.2" before "9.5"
        // because '1' < '9' as characters). Nulls sort to one consistent end
        // instead of being compared as NaN.
        sortComparator: (v1, v2) => {
          const a = v1 == null ? -Infinity : Number(v1)
          const b = v2 == null ? -Infinity : Number(v2)
          return a - b
        },
        align: 'right',
        headerAlign: 'right',
        valueGetter: (params) => params.row[d],
        cellClassName: heatmap ? 'b4p-heat-cell' : undefined,
        renderCell: (params) => {
          const v = params.value
          const text = v == null ? '—' : Number(v).toFixed(2) + (table.unit === '%' ? '' : '')
          const style = heatmap ? heatmapStyleFor(v, valueRange.min, valueRange.max, isRate) : null
          return (
            <Box sx={{
              width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              px: 1.25,
              bgcolor: style?.bg ?? 'transparent',
              color: v == null ? 'text.disabled' : (style?.color ?? 'inherit'),
            }}>
              {text}
            </Box>
          )
        },
      })),
    ]

    const rs = table.rows.map((r, i) => ({
      id: r.groupKey ?? `row-${i}`,
      groupKey: r.groupKey,
      ...r.values,
    }))

    return { columns: cols, rows: rs }
  }, [table, aggMode, heatmap, valueRange])

  // Exports the FULL table (every group row × every date column), not just
  // the rows currently visible on the DataGrid's page — the grid's own
  // pagination only limits what's rendered on screen, the underlying
  // `table` data already holds everything the backend returned.
  const exportCsv = () => {
    if (!table || !table.rows?.length) return

    const escape = (v) => {
      const s = String(v ?? '')
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }

    const header = [aggMode || 'Group', ...table.dates]
    const lines = [header.map(escape).join(',')]

    table.rows.forEach(r => {
      const cells = [
        r.groupKey,
        ...table.dates.map(d => {
          const v = r.values?.[d]
          return v == null ? '' : Number(v).toFixed(2)
        }),
      ]
      lines.push(cells.map(escape).join(','))
    })

    // Leading BOM so Excel opens the file as UTF-8 instead of guessing wrong.
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const safeName = `${table.kpiName || 'kpi'}_${aggMode || 'group'}`.replace(/[^a-z0-9]+/gi, '_')

    const a = document.createElement('a')
    a.href = url
    a.download = `${safeName}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Shared header bar — used both inline and inside the maximized dialog.
  // `onMaximize` / `onClose` are mutually exclusive: pass whichever applies.
  const renderHeaderBar = ({ onMaximize, onClose }) => (
    <Box sx={{
      px: 1.5, py: 0.75,
      bgcolor: 'grey.100',
      borderBottom: '1px solid', borderColor: 'divider',
      display: 'flex', alignItems: 'center', gap: 1,
      flexShrink: 0,
    }}>
      <TableChartIcon sx={{ fontSize: 14, color: 'text.secondary' }} />

      {/* KPI picker — defaults to Main KPI after Apply; switching it refetches only the table */}
      <FormControl size="small">
        <Select
          value={kpiOptions.some(k => k.key === selectedKpi) ? selectedKpi : ''}
          onChange={e => onChangeKpi(e.target.value)}
          displayEmpty
          disabled={!kpiOptions.length}
          sx={{
            height: 24, fontSize: 11, minWidth: 220, bgcolor: 'background.paper',
            '& .MuiSelect-select': { py: 0.25 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
          }}
        >
          <MenuItem value="" disabled sx={{ fontSize: 11, color: 'text.disabled' }}>
            (Select KPI)
          </MenuItem>
          {kpiOptions.map(k => (
            <MenuItem key={k.key} value={k.key} sx={{ fontSize: 11 }}>
              {k.label}{k.key === mainKpi ? '  · main' : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>by</Typography>

      {/* Aggregation Mode picker — moved here from the FilterBar so all
          table-specific controls live in one place; instant refetch on change. */}
      <FormControl size="small">
        <Select
          value={aggMode || ''}
          onChange={e => onChangeAggMode(e.target.value)}
          sx={{
            height: 24, fontSize: 11, minWidth: 120, bgcolor: 'background.paper',
            '& .MuiSelect-select': { py: 0.25 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
          }}
        >
          {AGG_MODES.map(m => (
            <MenuItem key={m} value={m} sx={{ fontSize: 11 }}>{m}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {table?.unit && (
        <Typography sx={{ fontSize: 10, color: 'text.disabled', ml: 0.5 }}>
          ({table.unit})
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <CircularProgress size={11} />
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Loading…</Typography>
        </Box>
      )}
      {!loading && table && (
        <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
          {rows.length} rows · {table.dates.length} dates
        </Typography>
      )}

      <Tooltip title={
        (heatmap ? 'Hide' : 'Show') +
        ' heatmap coloring. Light = low value, dark = high value (based on the 5th-95th percentile — extreme outliers show fully saturated). Green = higher is better, red = lower is better.'
      }>
        <IconButton size="small" onClick={() => setHeatmap(h => !h)}
          sx={{ p: 0.25, color: heatmap ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>
          <GradientIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Export all rows to CSV">
        <span>
          <IconButton size="small" onClick={exportCsv} disabled={!table || rows.length === 0}
            sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
            <DownloadIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>

      {onMaximize && (
        <Tooltip title="Maximize table">
          <IconButton size="small" onClick={onMaximize} sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
            <FullscreenIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
      {onClose && (
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )

  // Body — swaps between empty / data. Loading is shown in the header bar
  // so the dropdown stays usable; the body keeps its previous content if any.
  const renderBody = () => (
    <Box sx={{ minHeight: 0, width: '100%', display: 'flex', alignItems: 'flex-start' }}>
      {!table || rows.length === 0 ? (
        <Box sx={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          color: 'text.disabled', p: 4, textAlign: 'center',
        }}>
          <TableChartIcon sx={{ fontSize: 36, opacity: 0.3 }} />
          <Typography sx={{ fontSize: 12, mt: 0.5 }}>
            {loading ? 'Loading table…' : 'No table data yet — pick a KPI and press Apply.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            density="compact"
            disableRowSelectionOnClick
            // Sizes the grid to exactly fit the current page's rows (25/50/100,
            // whatever "Rows per page" is set to) instead of a fixed container
            // height with its own internal scrollbar cutting a page in half.
            // The page as a whole scrolls if this makes the table taller than
            // the viewport — see the overflowY:'auto' on the outer
            // Beacon4PerformanceView container.
            autoHeight
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              // This is the actual freezing mechanism — pins the group-key
              // column (both its header and every body cell) to the left
              // edge of the grid so it stays visible while scrolling
              // horizontally through the date columns.
              pinnedColumns: { left: [GROUP_COL_FIELD] },
            }}
            loading={loading}
            sx={gridSx}
          />
        </Box>
      )}
    </Box>
  )

  return (
    <>
      <Paper sx={{
        overflow: 'hidden',
        height: '100%', display: 'flex', flexDirection: 'column',
      }}>
        {renderHeaderBar({ onMaximize: () => setMaximized(true) })}
        {renderBody()}
      </Paper>

      {/* Maximized view — same table, full screen. Re-rendered (not just
          CSS-scaled) so the DataGrid measures the larger viewport correctly. */}
      <Dialog
        open={maximized}
        onClose={() => setMaximized(false)}
        fullScreen
        PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}
      >
        {renderHeaderBar({ onClose: () => setMaximized(false) })}
        {renderBody()}
      </Dialog>
    </>
  )
}